import React, { useEffect, useState } from 'react';

export const MATERIAL_AVATAR_SOURCES = [
  '/avatars/material/cobalt-glass.webp',
  '/avatars/material/apricot-paper.webp',
  '/avatars/material/plum-velvet.webp',
  '/avatars/material/teal-gel.webp',
  '/avatars/material/pearlescent-ceramic.webp',
  '/avatars/material/mint-acrylic.webp',
  '/avatars/material/coral-satin.webp',
  '/avatars/material/graphite-metal.webp',
  '/avatars/material/indigo-clay.webp',
  '/avatars/material/rose-resin.webp',
] as const;

/**
 * FNV-1a gives each stable record ID the same avatar across sessions without
 * storing a generated URL on the record itself.
 */
export const getMaterialAvatarSrc = (seed: string): string => {
  let hash = 0x811c9dc5;

  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }

  return MATERIAL_AVATAR_SOURCES[(hash >>> 0) % MATERIAL_AVATAR_SOURCES.length];
};

interface MaterialAvatarProps {
  seed: string;
  name: string;
  src?: string | null;
  className?: string;
  loading?: 'eager' | 'lazy';
}

const MaterialAvatar: React.FC<MaterialAvatarProps> = ({
  seed,
  name,
  src,
  className = 'h-10 w-10',
  loading = 'lazy',
}) => {
  const [imageFailed, setImageFailed] = useState(false);
  const source = src || getMaterialAvatarSrc(seed || name || 'avatar');

  useEffect(() => {
    setImageFailed(false);
  }, [source]);

  return (
    <div
      className={`${className} relative grid shrink-0 place-items-center overflow-hidden rounded-full bg-zinc-200`}
    >
      {imageFailed ? (
        <span
          aria-hidden="true"
          className="text-sm font-semibold text-zinc-700"
        >
          {name.charAt(0).toUpperCase() || '?'}
        </span>
      ) : (
        <img
          src={source}
          alt={`${name || 'User'} avatar`}
          loading={loading}
          decoding="async"
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      )}
    </div>
  );
};

export default MaterialAvatar;
